// @ts-nocheck

import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { getDashboardStats, getTokenUsage, getDailyTokenUsage, getCitationSettings, getCitationAnalytics, getRecentCitationsPaginated } from '../services/api';
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Pie, Line, Bar } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from '../components/common/Loader';
import CitationAnalytics from '../components/analytics/CitationAnalytics';
import { 
  Button, Box, Alert, CircularProgress, Typography, Paper, 
  Pagination, Collapse, IconButton
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCog, 
    faChevronDown, 
    faChevronUp, 
    faChartBar, 
    faDollarSign, 
    faQuoteRight,
    faLayerGroup,
    faCalendarCheck,
    faChartLine,
    faTrendUp,
    faMessages,
    faFileText
} from '@fortawesome/free-solid-svg-icons';

// Register ALL required ChartJS components
ChartJS.register(
    ArcElement,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface UnifiedAnalyticsProps {
    clientId: string | null;
}

const UnifiedAnalytics: React.FC<UnifiedAnalyticsProps> = ({ clientId }) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Dashboard Stats
    const [dashboardStats, setDashboardStats] = useState({
        totalInteractions: 0,
        successfulBookings: 0,
        chatInteractions: 0,
        queryCategories: {},
        dailyInteractions: {},
        citationCount: 0,
        uniqueSources: 0,
        tokenCost: 0,
        tokenRequests: 0
    });
    
    // Token Usage Data
    const [tokenUsage, setTokenUsage] = useState(null);
    const [dailyUsage, setDailyUsage] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(30);
    
    // Citation Analytics Data
    const [citationsEnabled, setCitationsEnabled] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [recentCitations, setRecentCitations] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    
    // Section visibility state
    const [expandedSections, setExpandedSections] = useState({
        overview: true,
        usage: false,
        citations: false
    });
    
    const userRole = localStorage.getItem('user_role');
    const ROWS_PER_PAGE = 10;

    // Toggle section visibility
    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Smooth scroll to section and expand it
    const scrollToSection = (section: string) => {
        // First expand the section if it's not already expanded
        if (!expandedSections[section]) {
            setExpandedSections(prev => ({
                ...prev,
                [section]: true
            }));
        }
        
        // Wait a bit for the section to expand, then scroll
        setTimeout(() => {
            const element = document.getElementById(`${section}-section`);
            if (element) {
                element.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                });
            }
        }, 100);
    };

    // Track token loading separately to avoid page scroll
    const [tokenLoading, setTokenLoading] = useState(false);

    // Fetch initial data (everything except token usage)
    useEffect(() => {
        const fetchInitialData = async () => {
            setIsLoading(true);
            try {
                // Fetch dashboard stats
                let dashboardData;
                if (userRole === 'consultant') {
                    dashboardData = await getDashboardStats(null);
                } else {
                    if (!clientId) {
                        setError('No client ID available');
                        setIsLoading(false);
                        return;
                    }
                    dashboardData = await getDashboardStats(clientId);
                }
                setDashboardStats(dashboardData);

                // Fetch citation data
                if (clientId) {
                    try {
                        const [settingsRes, analyticsRes] = await Promise.all([
                            getCitationSettings(clientId),
                            getCitationAnalytics(clientId)
                        ]);

                        if (settingsRes.success) {
                            setCitationsEnabled(settingsRes.citations_enabled);
                        }

                        if (analyticsRes.success) {
                            setAnalyticsData(analyticsRes);
                        }

                        // Fetch recent citations if enabled
                        if (settingsRes.success && settingsRes.citations_enabled) {
                            const recentRes = await getRecentCitationsPaginated(clientId, 1, ROWS_PER_PAGE);
                            if (recentRes.success) {
                                setRecentCitations(recentRes.data || []);
                                setTotalPages(recentRes.pagination?.total_pages || 0);
                                setTotalCount(recentRes.pagination?.total_count || 0);
                            }
                        }
                    } catch (citationError) {
                        console.error('Error fetching citation data:', citationError);
                    }
                }

                setError(null);
            } catch (err) {
                console.error('Error fetching analytics data:', err);
                const errorMessage = err.response?.data?.detail || 'Failed to fetch analytics data';
                toast.error(errorMessage);
                setError(errorMessage);
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, [clientId, userRole]);

    // Separate effect for token usage that doesn't cause page scroll
    useEffect(() => {
        const fetchTokenUsage = async () => {
            if (userRole !== 'consultant' && clientId) {
                setTokenLoading(true);
                try {
                    const [tokenData, dailyData] = await Promise.all([
                        getTokenUsage(clientId, selectedPeriod),
                        getDailyTokenUsage(clientId, 7)
                    ]);
                    setTokenUsage(tokenData);
                    setDailyUsage(dailyData);
                } catch (tokenError) {
                    console.error('Error fetching token data:', tokenError);
                } finally {
                    setTokenLoading(false);
                }
            }
        };

        fetchTokenUsage();
    }, [clientId, userRole, selectedPeriod]);

    // Clean Metric Cards Component matching sidebar theme
    const MetricCard = ({ title, value, subtitle, className = "", onClick = null, icon = null, trend = null }) => {
        return (
            <div 
                className={`
                    group relative bg-white rounded-xl shadow-lg border border-gray-100
                    p-4 sm:p-6 transition-all duration-300 ease-out hover:shadow-xl
                    ${onClick ? 'cursor-pointer hover:scale-105 hover:-translate-y-1' : ''}
                    ${className}
                `}
                onClick={onClick}
            >
                {/* Triangle corner badge - like e-commerce corner ribbons */}
                {trend && (
                    <div className="absolute top-0 right-0 w-0 h-0 z-10">
                        {/* Triangle background */}
                        <div 
                            className="absolute top-0 right-0 w-16 h-16"
                            style={{
                                backgroundColor: 'rgb(234, 179, 8)',
                                clipPath: 'polygon(100% 0, 0 0, 100% 100%)'
                            }}
                        ></div>
                        {/* Badge content */}
                        <div className="absolute top-2 right-2 text-white text-xs font-bold transform rotate-45">
                            <div className="text-center leading-tight">
                                <div className="text-xs">{trend.value}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Content */}
                <div className="relative">
                    {/* Header row with title and icon */}
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                        {icon && (
                            <div className="p-2 sm:p-2.5 rounded-xl transition-transform group-hover:scale-110 flex-shrink-0" style={{ backgroundColor: 'rgb(234, 179, 8)' }}>
                                <div className="w-4 h-4 sm:w-5 sm:h-5 text-white">
                                    {icon}
                                </div>
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <h3 className="text-gray-600 text-xs font-semibold tracking-wide uppercase leading-tight">
                                {title}
                            </h3>
                        </div>
                    </div>

                    {/* Main value */}
                    <div className="mb-3">
                        <div className="flex items-baseline space-x-2">
                            <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#065BDF] transition-transform group-hover:scale-105">
                                {typeof value === 'string' && value.startsWith('$') ? value : value.toString().padStart(2, '0')}
                            </span>
                        </div>
                    </div>

                    {/* Subtitle */}
                    <div className="flex items-center justify-between">
                        <p className="text-gray-600 text-xs sm:text-sm font-medium">
                            {subtitle}
                        </p>
                        {onClick && (
                            <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgb(234, 179, 8)' }}>
                                    <span className="text-white text-xs sm:text-sm">⋯</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Progress bar (optional) */}
                    {value !== 0 && typeof value === 'number' && (
                        <div className="mt-3 sm:mt-4">
                            <div className="w-full bg-gray-100 rounded-full h-1.5">
                                <div 
                                    className="h-1.5 rounded-full transition-all duration-1000 ease-out"
                                    style={{ 
                                        backgroundColor: 'rgb(234, 179, 8)',
                                        width: `${Math.min(value / 20 * 100, 100)}%` 
                                    }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Section Header Component
    const SectionHeader = ({ title, icon, expanded, onToggle, count = null }) => (
        <div 
            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={onToggle}
        >
            <div className="flex items-center space-x-3">
                <FontAwesomeIcon 
                    icon={icon} 
                    className="text-xl" 
                    style={{ color: 'rgb(234, 179, 8)' }}
                />
                <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                {count !== null && (
                    <span className="text-sm px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                        {count} items
                    </span>
                )}
            </div>
            <IconButton size="small">
                <FontAwesomeIcon 
                    icon={expanded ? faChevronUp : faChevronDown} 
                    style={{ color: 'rgb(234, 179, 8)' }}
                />
            </IconButton>
        </div>
    );

    // Prepare chart data
    const prepareDashboardCharts = () => {
        // Daily interactions chart
        const dailyData = Object.entries(dashboardStats.dailyInteractions || {})
            .map(([date, count]) => ({
                date: new Date(date).toLocaleDateString(),
                count: Number(count)
            }));

        if (dailyData.length === 1) {
            const currentDate = new Date(Object.keys(dashboardStats.dailyInteractions)[0]);
            for (let i = 3; i > 0; i--) {
                const date = new Date(currentDate);
                date.setDate(date.getDate() - i);
                dailyData.unshift({
                    date: date.toLocaleDateString(),
                    count: 0
                });
            }
            for (let i = 1; i <= 3; i++) {
                const date = new Date(currentDate);
                date.setDate(date.getDate() + i);
                dailyData.push({
                    date: date.toLocaleDateString(),
                    count: 0
                });
            }
        }

        dailyData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const lineChartData = {
            labels: dailyData.map(item => item.date),
            datasets: [
                {
                    label: 'Daily Interactions',
                    data: dailyData.map(item => item.count),
                    borderColor: '#1E88E5',
                    backgroundColor: 'rgba(30, 136, 229, 0.1)',
                    tension: 0.4,
                    fill: true,
                    pointRadius: 4,
                    pointBackgroundColor: '#1E88E5'
                }
            ]
        };

        // Pie chart data
        const categoryDisplayNames: Record<string, string> = {
            embeddings: 'knowledge',
            knowledge_fallback: 'knowledge fallback',
        };

        const pieChartData = {
            labels: Object.keys(dashboardStats.queryCategories).map(k => categoryDisplayNames[k] ?? k.replace(/_/g, ' ')),
            datasets: [{
                data: Object.values(dashboardStats.queryCategories),
                backgroundColor: [
                    '#FFC107', '#FF4081', '#2196F3', '#757575', '#9C27B0'
                ],
                borderWidth: 0
            }]
        };

        return { lineChartData, pieChartData };
    };

    // Prepare token usage charts
    const prepareTokenCharts = () => {
        if (!tokenUsage || !dailyUsage) return { serviceChartData: null, dailyChartData: null };

        const serviceChartData = {
            labels: tokenUsage?.services?.map(service => service.service_type.charAt(0).toUpperCase() + service.service_type.slice(1)) || [],
            datasets: [
                {
                    label: 'Cost ($)',
                    data: tokenUsage?.services?.map(service => service.total_cost) || [],
                    backgroundColor: [
                        'rgba(99, 102, 241, 0.8)',
                        'rgba(16, 185, 129, 0.8)',
                        'rgba(245, 158, 11, 0.8)',
                        'rgba(239, 68, 68, 0.8)',
                        'rgba(139, 92, 246, 0.8)'
                    ],
                    borderWidth: 1,
                },
            ],
        };

        const dailyChartData = {
            labels: dailyUsage?.daily_usage?.map(day => 
                new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            ) || [],
            datasets: [
                {
                    label: 'Daily Cost ($)',
                    data: dailyUsage?.daily_usage?.map(day => day.total_cost) || [],
                    backgroundColor: 'rgba(99, 102, 241, 0.8)',
                    borderColor: 'rgba(99, 102, 241, 1)',
                    borderWidth: 1,
                },
            ],
        };

        return { serviceChartData, dailyChartData };
    };

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="h-full min-h-[600px] relative">
                <Loader 
                    fullPage={false} 
                    text="Loading your analytics..." 
                    variant="pulse"
                    overlay={true} 
                />
            </div>
        );
    }

    const { lineChartData, pieChartData } = prepareDashboardCharts();
    const { serviceChartData, dailyChartData } = prepareTokenCharts();

    const lineChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                ticks: { stepSize: 1, precision: 0 }
            },
            x: { grid: { display: false } }
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function(context) {
                        return `Interactions: ${context.parsed.y}`;
                    }
                }
            }
        }
    };

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right' as const,
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: { size: 12 }
                }
            }
        }
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold">Dashboard</h1>
                {/* <div className="flex items-center space-x-4">
                    <Button 
                        component={Link} 
                        to="/settings" 
                        variant="outlined" 
                        color="primary" 
                        startIcon={<FontAwesomeIcon icon={faCog} />}
                    >
                        Settings
                    </Button>
                </div> */}
            </div>

            <div className="space-y-6">
                {/* Quick Overview Cards - Always Visible */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
                    <MetricCard 
                        title="Total Interactions"
                        value={dashboardStats.totalInteractions}
                        subtitle="Total Queries"
                        icon={<FontAwesomeIcon icon={faChartBar} />}
                        trend={{ value: "+12%", direction: "up" }}
                    />
                    <MetricCard 
                        title="Total Citations"
                        value={dashboardStats.citationCount}
                        subtitle="Citations Used"
                        icon={<FontAwesomeIcon icon={faQuoteRight} />}
                    />
                    <MetricCard 
                        title="Unique Sources"
                        value={dashboardStats.uniqueSources}
                        subtitle="Distinct Sources Cited"
                        icon={<FontAwesomeIcon icon={faLayerGroup} />}
                    />
                    <MetricCard 
                        title="Successful Bookings"
                        value={dashboardStats.successfulBookings}
                        subtitle="Completed Bookings"
                        icon={<FontAwesomeIcon icon={faCalendarCheck} />}
                    />
                    {/* {userRole !== 'consultant' && (
                        <MetricCard
                            title="AI Usage Cost"
                            value={`$${dashboardStats.tokenCost?.toFixed(2) || '0.00'}`}
                            subtitle="This Month"
                            icon={<FontAwesomeIcon icon={faDollarSign} />}
                            trend={{ value: "+5%", direction: "up" }}
                            onClick={() => scrollToSection('usage')}
                        />
                    )} */}
                </div>

                {/* Performance Overview Section */}
                <div className="space-y-4">
                    <SectionHeader 
                        title="Performance Overview"
                        icon={faChartBar}
                        expanded={expandedSections.overview}
                        onToggle={() => toggleSection('overview')}
                    />
                    
                    <Collapse in={expandedSections.overview}>
                        <div className="space-y-6">
                            {/* Charts Grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Daily Interactions Chart */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    <h3 className="text-lg font-medium mb-4">Daily Interactions</h3>
                                    <div className="h-[300px] relative">
                                        {Object.keys(dashboardStats.dailyInteractions || {}).length === 0 ? (
                                            <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                                                No interaction data available
                                            </div>
                                        ) : (
                                            <Line 
                                                data={lineChartData}
                                                options={lineChartOptions}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Query Categories Chart */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    <h3 className="text-lg font-medium mb-4">Query Categories</h3>
                                    <div className="h-[300px]">
                                        <Pie 
                                            data={pieChartData}
                                            options={pieChartOptions}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Collapse>
                </div>

                {/* Usage & Costs Section - Only for non-consultants */}
                {false && userRole !== 'consultant' && (
                    <div className="space-y-4" id="usage-section">
                        <SectionHeader 
                            title="Usage & Costs"
                            icon={faDollarSign}
                            expanded={expandedSections.usage}
                            onToggle={() => toggleSection('usage')}
                        />
                        
                        <Collapse in={expandedSections.usage}>
                            <div className="space-y-6">
                                {/* Period Selector */}
                                <div className="flex justify-end items-center space-x-3">
                                    {tokenLoading && (
                                        <div className="flex items-center space-x-2 text-sm text-gray-500">
                                            <CircularProgress size={16} />
                                            <span>Loading...</span>
                                        </div>
                                    )}
                                    <select
                                        value={selectedPeriod}
                                        onChange={(e) => setSelectedPeriod(Number(e.target.value))}
                                        disabled={tokenLoading}
                                        className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <option value={7}>Last 7 days</option>
                                        <option value={30}>Last 30 days</option>
                                        <option value={90}>Last 90 days</option>
                                    </select>
                                </div>

                                {/* Token Usage Summary Cards */}
                                {tokenUsage && !tokenLoading && (
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="p-5">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0">
                                                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                                            <span className="text-white text-sm font-medium">$</span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-5 w-0 flex-1">
                                                        <dl>
                                                            <dt className="text-sm font-medium text-gray-500 truncate">Total Cost</dt>
                                                            <dd className="text-lg font-medium text-gray-900">
                                                                ${tokenUsage?.total_cost?.toFixed(4) || '0.0000'}
                                                            </dd>
                                                        </dl>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="p-5">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0">
                                                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                                            <span className="text-white text-sm font-medium">T</span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-5 w-0 flex-1">
                                                        <dl>
                                                            <dt className="text-sm font-medium text-gray-500 truncate">Total Tokens</dt>
                                                            <dd className="text-lg font-medium text-gray-900">
                                                                {tokenUsage?.total_tokens?.toLocaleString() || '0'}
                                                            </dd>
                                                        </dl>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="p-5">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0">
                                                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                                                            <span className="text-white text-sm font-medium">R</span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-5 w-0 flex-1">
                                                        <dl>
                                                            <dt className="text-sm font-medium text-gray-500 truncate">Total Requests</dt>
                                                            <dd className="text-lg font-medium text-gray-900">
                                                                {tokenUsage?.total_requests?.toLocaleString() || '0'}
                                                            </dd>
                                                        </dl>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white overflow-hidden shadow rounded-lg">
                                            <div className="p-5">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0">
                                                        <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgb(234, 179, 8)' }}>
                                                            <span className="text-white text-sm font-medium">Avg</span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-5 w-0 flex-1">
                                                        <dl>
                                                            <dt className="text-sm font-medium text-gray-500 truncate">Avg Cost/Request</dt>
                                                            <dd className="text-lg font-medium text-gray-900">
                                                                ${tokenUsage?.total_requests ? (tokenUsage.total_cost / tokenUsage.total_requests).toFixed(4) : '0.0000'}
                                                            </dd>
                                                        </dl>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Token Usage Charts */}
                                {serviceChartData && dailyChartData && !tokenLoading && (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {/* Service Breakdown Chart */}
                                        <div className="bg-white p-6 rounded-lg shadow">
                                            <h3 className="text-lg font-medium text-gray-900 mb-4">Cost by Service Type</h3>
                                            <div className="h-64">
                                                <Pie data={serviceChartData} options={{ responsive: true, maintainAspectRatio: false }} />
                                            </div>
                                        </div>

                                        {/* Daily Usage Chart */}
                                        <div className="bg-white p-6 rounded-lg shadow">
                                            <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Usage Trend</h3>
                                            <div className="h-64">
                                                <Bar data={dailyChartData} options={{ responsive: true, maintainAspectRatio: false, scales: { y: { beginAtZero: true } } }} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Loading state for token data */}
                                {tokenLoading && (
                                    <div className="bg-white shadow rounded-lg p-12">
                                        <div className="flex flex-col items-center justify-center space-y-4">
                                            <CircularProgress size={40} />
                                            <div className="text-gray-500 text-center">
                                                <div className="font-medium">Loading Usage Data</div>
                                                <div className="text-sm">Fetching data for the selected period...</div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Service Details Table */}
                                {tokenUsage?.services && !tokenLoading && (
                                    <div className="bg-white shadow rounded-lg">
                                        <div className="px-4 py-5 sm:p-6">
                                            <h3 className="text-lg font-medium text-gray-900 mb-4">Service Usage Details</h3>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Type</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requests</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Tokens</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Cost</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Cost/Request</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {tokenUsage.services.map((service, index) => (
                                                            <tr key={index} className="hover:bg-gray-50">
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                    <span className="capitalize">{service.service_type}</span>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                    {service.request_count.toLocaleString()}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                    {service.total_tokens.toLocaleString()}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                    ${service.total_cost.toFixed(4)}
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                    ${service.avg_cost_per_request.toFixed(4)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Collapse>
                    </div>
                )}

                {/* Citations & Queries Section */}
                <div className="space-y-4">
                    <SectionHeader 
                        title="Citations & Queries"
                        icon={faQuoteRight}
                        expanded={expandedSections.citations}
                        onToggle={() => toggleSection('citations')}
                        count={citationsEnabled ? totalCount : null}
                    />
                    
                    <Collapse in={expandedSections.citations}>
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            {!clientId ? (
                                <Typography>Client ID not found. Please ensure you are logged in or provide a client ID.</Typography>
                            ) : citationsEnabled && analyticsData ? (
                                <CitationAnalytics 
                                    clientId={clientId} 
                                    analyticsData={analyticsData}
                                    recentCitations={recentCitations}
                                    loadingRecent={false}
                                    recentError={null}
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={(event, value) => setCurrentPage(value)}
                                    totalRecentQueryCount={totalCount}
                                />
                            ) : !citationsEnabled && clientId ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="text-gray-500 text-center">
                                        <div className="text-lg font-medium mb-2">Citations Not Enabled</div>
                                        <div className="text-sm">Citation analytics are not available because citations are disabled for this client.</div>
                                        <div className="text-sm mt-2">Enable citations to view citation analytics and source tracking.</div>
                                    </div>
                                </div>
                            ) : (
                                <Typography>Could not load analytics data. Please try again later.</Typography>
                            )}
                        </div>
                    </Collapse>
                </div>
            </div>
        </div>
    );
};

export default UnifiedAnalytics;